// SkyModeController.js

const SUN_SVG = `
<svg class="svg-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="4"/>
  <path d="M12 2v2"/>
  <path d="M12 20v2"/>
  <path d="m4.93 4.93 1.41 1.41"/>
  <path d="m17.66 17.66 1.41 1.41"/>
  <path d="M2 12h2"/>
  <path d="M20 12h2"/>
  <path d="m6.34 17.66-1.41 1.41"/>
  <path d="m19.07 4.93-1.41 1.41"/>
</svg>`;

class SkyModeController {
    constructor() {
        this._currentMode = "light";
        this._initialized = false;
        this._userOverride = false;
        this._pendingSkyMode = false;

        // Day/night hero-video swap map (legacy #character-panel only).
        // OBSOLETE as of the transparent-card-video migration: hero cards now
        // ship a single transparent clip per legend (same file for light and
        // dark, served via legends.json), so there is no separate WB/WW file to
        // swap to. The old WB.mp4 -> WW.mp4 entries named files that have since
        // been deleted, so the map is intentionally empty — updateCharacterVideos
        // becomes a no-op (it keeps whatever <source> the card already has).
        this._videoMappings = {};

        // Reverse mapping for switching from light to dark
        this._reverseMappings = {};
        for (const [dark, light] of Object.entries(this._videoMappings)) {
            this._reverseMappings[light] = dark;
        }
    }

    initialize() {
        if (this._initialized) return;

        this._initialized = true;

        this.injectThemeStyles();

        // Demo deep link (&hour=morning|dusk): a session-scoped pin set by the
        // page <head> bootstrap beats the OS preference so a capture URL always
        // reproduces the same lighting. Marked as a user override so nothing
        // can flip it back for the session.
        const hourPin =
            typeof window !== "undefined" &&
            (window.LL_HOUR_PIN === "light" || window.LL_HOUR_PIN === "dark")
                ? window.LL_HOUR_PIN
                : null;
        if (hourPin) {
            this._currentMode = hourPin;
            this._userOverride = true;
            console.log(
                "[SkyModeController] Hour pinned for this session via deep link:",
                hourPin,
            );
        } else {
            // Always use OS preference on page load - ignore any saved user preferences
            this._currentMode = this.getSystemPreference();
            this._userOverride = false;
            console.log(
                "[SkyModeController] Using OS preference on page load:",
                this._currentMode,
            );
        }

        this.updateButtonStates();
        this.applyUITheme();
        this.applySkyMode();

        // Initialize resize listener for video sync on viewport changes
        this.initResizeListener();

        console.log(
            "[SkyModeController] Initialized with",
            this._currentMode,
            "mode (checked once on page load)",
        );
    }

    applySkyMode() {
        if (window.modelViewer && window.modelViewer.setSkyMode) {
            try {
                window.modelViewer.setSkyMode(this._currentMode, false);
                console.log(
                    "[SkyModeController] Applied sky mode on init:",
                    this._currentMode,
                );
            } catch (e) {
                console.warn(
                    "[SkyModeController] Could not apply sky mode on init:",
                    e.message,
                );
            }
        } else {
            console.log(
                "[SkyModeController] ModelViewer not ready, will retry when available",
            );
            this._pendingSkyMode = true;
        }
    }

    onModelViewerReady() {
        if (
            this._pendingSkyMode &&
            window.modelViewer &&
            window.modelViewer.setSkyMode
        ) {
            try {
                window.modelViewer.setSkyMode(this._currentMode, false);
                this._pendingSkyMode = false;
                console.log(
                    "[SkyModeController] Applied pending sky mode:",
                    this._currentMode,
                );
            } catch (e) {
                console.warn(
                    "[SkyModeController] Could not apply pending sky mode:",
                    e.message,
                );
            }
        }
    }

    getSystemPreference() {
        if (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
            return "dark";
        }
        return "light";
    }

    injectThemeStyles() {
        if (document.getElementById("sky-mode-theme-styles")) return;

        const styleEl = document.createElement("style");
        styleEl.id = "sky-mode-theme-styles";
        styleEl.textContent = `
            /* ==========================================
               CHARACTER PANEL - Light Mode Overrides
               ========================================== */

               #sky-mode-icon svg {
                   display: block;
               }
            
            /* Panel header and drag area */
            body.light-mode #character-panel .panel-drag-area,
            body.light-mode #character-panel .panel-header,
            body.light-mode #character-panel .panel-header span,
            body.light-mode #character-panel .panel-header i {
                color: #000000 !important;
            }
            
            /* Panel buttons (swap/settings tabs) */
            body.light-mode #character-panel .panel-button,
            body.light-mode #character-panel .panel-button span,
            body.light-mode #character-panel .panel-button i {
                color: #000000 !important;
                background: rgba(255, 255, 255, 0.8) !important;
                border-color: rgba(0, 0, 0, 0.1) !important;
            }
            
            body.light-mode #character-panel .panel-button:hover {
                background: rgba(255, 255, 255, 0.95) !important;
            }
            
            body.light-mode #character-panel .panel-button.active {
                background: rgba(255, 215, 0, 0.3) !important;
                border-color: rgba(255, 215, 0, 0.5) !important;
            }
            
            /* Category filter buttons */
            body.light-mode #character-panel .category-filter-btn,
            body.light-mode #character-panel .category-filter-btn span,
            body.light-mode #character-panel .category-filter-btn i {
                color: #000000 !important;
                background: rgba(255, 255, 255, 0.8) !important;
                border-color: rgba(0, 0, 0, 0.15) !important;
            }
            
            body.light-mode #character-panel .category-filter-btn:hover {
                background: rgba(255, 255, 255, 0.95) !important;
            }
            
            body.light-mode #character-panel .category-filter-btn.active {
                background: rgba(58, 65, 73, 0.3) !important;
                border-color: rgba(58, 65, 73, 0.5) !important;
            }
            
            /* Field titles and section headers */
            body.light-mode #character-panel .field-title,
            body.light-mode #character-panel .field-section,
            body.light-mode #character-panel .field-section span {
                color: #000000 !important;
            }
            
            /* Character cards - Light Mode (white background) */
            body.light-mode #character-panel .character-item {
                background: rgba(255, 255, 255, 1) !important;
                border-color: rgba(0, 0, 0, 0.1) !important;
            }
            
            body.light-mode #character-panel .character-item:hover {
                background: rgba(255, 255, 255, 1) !important;
            }
            
            /* Character cards - Dark Mode (black background) */
            body.dark-mode #character-panel .character-item {
                background: rgba(0, 0, 0, 1) !important;
                border-color: rgba(255, 255, 255, 0.1) !important;
            }
            
            body.dark-mode #character-panel .character-item:hover {
                background: rgba(0, 0, 0, 1) !important;
            }
            
            /* Character media container - Light Mode (pure white) */
            body.light-mode #character-panel .character-media {
                background: #ffffff !important;
            }
            
            /* Character media container - Dark Mode (pure black) */
            body.dark-mode #character-panel .character-media {
                background: #000000 !important;
            }
            
            /* Character video background - Light Mode (white to match card) */
            body.light-mode #character-panel .character-video {
                background: #ffffff !important;
            }
            
            /* Character video background - Dark Mode (black to match card) */
            body.dark-mode #character-panel .character-video {
                background: #000000 !important;
            }
            
            /* Character card text elements */
            body.light-mode #character-panel .character-name,
            body.light-mode #character-panel .character-tagline,
            body.light-mode #character-panel .character-description,
            body.light-mode #character-panel .character-meta,
            body.light-mode #character-panel .character-label,
            body.light-mode #character-panel .character-info,
            body.light-mode #character-panel .character-info h4,
            body.light-mode #character-panel .character-info p,
            body.light-mode #character-panel .character-info strong {
                color: #000000 !important;
            }
            
            /* Empty state message */
            body.light-mode #character-panel .panel-empty-state,
            body.light-mode #character-panel .panel-empty-state span,
            body.light-mode #character-panel .panel-empty-state p {
                color: #000000 !important;
            }
            
            /* Search input field */
            body.light-mode #character-panel .character-search-container input {
                color: #000000 !important;
            }
            
            body.light-mode #character-panel .character-search-container input::placeholder {
                color: #000000 !important;
                opacity: 0.7;
            }
            
            body.light-mode #character-panel .character-search-container .fa-search {
                color: #000000 !important;
            }
            
            /* ==========================================
               PROFILE BUTTON - Light Mode Override
               ========================================== */
            
            body.light-mode .profile-button,
            body.light-mode .profile-button i {
                color: #000000 !important;
            }
            
            /* ==========================================
               LESSON HUB & TUTOR PANELS - Light Mode Override
               ========================================== */
            
            /* Lesson Hub (Historian) panel background */
            body.light-mode #hist-sheet {
                background: #f9f6f0 !important;
                backdrop-filter: none !important;
                -webkit-backdrop-filter: none !important;
            }
            
            /* Lesson Hub header - match panel background in light mode */
            body.light-mode #hist-topbar {
                background: #959490 !important;
                backdrop-filter: none !important;
                -webkit-backdrop-filter: none !important;
            }
            
            /* Lesson Hub muted text - change from #8B949E to black */
            body.light-mode .hist-muted {
                color: #000000 !important;
            }
            
            /* Lesson Hub loading animation text ("Preparing lesson...") */
            body.light-mode .hist-skeleton-status,
            body.light-mode .hist-skeleton-status span {
                color: #000000 !important;
            }
            
            /* Tutor panel background - white in light mode */
            body.light-mode #tutor-sheet {
                background: #ffffff !important;
            }
            
            /* Tutor container - white background in light mode */
            body.light-mode #tutor-container {
                background: #ffffff !important;
            }
            
            /* Tutor Hub header - pure white in light mode */
            body.light-mode #tutor-container .tutor-top-bar {
                background: #ffffff !important;
                backdrop-filter: none !important;
                -webkit-backdrop-filter: none !important;
            }
            
            /* Tutor main content area - white background */
            body.light-mode #tutor-container .tutor-main,
            body.light-mode #tutor-container .tutor-content,
            body.light-mode #tutor-container .tutor-body {
                background: #ffffff !important;
            }
            
            /* ==========================================
               TUTOR HUB - All Text Black in Light Mode
               ========================================== */
            
            /* Tutor container - all text elements */
            body.light-mode #tutor-container,
            body.light-mode #tutor-container h1,
            body.light-mode #tutor-container h2,
            body.light-mode #tutor-container h3,
            body.light-mode #tutor-container h4,
            body.light-mode #tutor-container h5,
            body.light-mode #tutor-container h6,
            body.light-mode #tutor-container p,
            body.light-mode #tutor-container span,
            body.light-mode #tutor-container label,
            body.light-mode #tutor-container li,
            body.light-mode #tutor-container strong,
            body.light-mode #tutor-container em,
            body.light-mode #tutor-container div {
                color: #000000 !important;
            }
            
            /* Tutor header elements */
            body.light-mode #tutor-container .tutor-header,
            body.light-mode #tutor-container .tutor-header h2,
            body.light-mode #tutor-container .tutor-header p,
            body.light-mode #tutor-container .tutor-page-title {
                color: #000000 !important;
                -webkit-text-fill-color: #000000 !important;
                background: none !important;
            }
            
            /* Tutor loading/skeleton text */
            body.light-mode #tutor-container .tutor-loading-text,
            body.light-mode #tutor-container .tutor-spinner,
            body.light-mode #tutor-container .tutor-skeleton-container {
                color: #000000 !important;
            }
            
            /* Tutor diagnostic, subtopic, quiz text */
            body.light-mode #tutor-container .tutor-question-header,
            body.light-mode #tutor-container .tutor-question-text,
            body.light-mode #tutor-container .tutor-option-text,
            body.light-mode #tutor-container .tutor-subtopic-title,
            body.light-mode #tutor-container .tutor-subtopic-item,
            body.light-mode #tutor-container .tutor-topic-text,
            body.light-mode #tutor-container .tutor-quiz-text {
                color: #000000 !important;
            }
            
            /* Tutor progress steps */
            body.light-mode #tutor-container .tutor-progress-step,
            body.light-mode #tutor-container .tutor-step-label {
                color: #000000 !important;
            }
            
            /* Tutor form labels and inputs - black text, white background, black border */
            body.light-mode #tutor-container .tutor-form-group label {
                color: #000000 !important;
            }
            
            body.light-mode #tutor-container .tutor-input,
            body.light-mode #tutor-container .tutor-select {
                color: #000000 !important;
                background: #ffffff !important;
                border: 1px solid #E5E0D8 !important;
            }
            
            /* Tutor cards and sections - white background in light mode */
            body.light-mode #tutor-container .tutor-card,
            body.light-mode #tutor-container .tutor-section,
            body.light-mode #tutor-container .tutor-view {
                color: #000000 !important;
                background: #ffffff !important;
            }
            
            /* Tutor question cards - white background */
            body.light-mode #tutor-container .tutor-question-card,
            body.light-mode #tutor-container .tutor-question-header {
                background: #ffffff !important;
            }
            
            /* Tutor MCQ options - white background */
            body.light-mode #tutor-container .tutor-mcq-option {
                background: #ffffff !important;
            }
            
            /* Tutor subtopic items - white background */
            body.light-mode #tutor-container .tutor-subtopic-item {
                background: #ffffff !important;
            }
            
            /* Tutor skeleton cards - white background */
            body.light-mode #tutor-container .tutor-skeleton-card {
                background: #ffffff !important;
            }
            
            /* Tutor progress steps - white background */
            body.light-mode #tutor-container .tutor-progress-step {
                background: #ffffff !important;
            }
            
            /* Tutor results and evaluation text */
            body.light-mode #tutor-container .tutor-result-text,
            body.light-mode #tutor-container .tutor-evaluation,
            body.light-mode #tutor-container .tutor-score,
            body.light-mode #tutor-container .tutor-feedback {
                color: #000000 !important;
            }
            
            /* Tutor diagnostic score circle - white background in light mode */
            body.light-mode #tutor-container .tutor-score-inner {
                background: #ffffff !important;
            }
            
            /* Answer via Chat / Exit Chat Mode buttons - black text and black border in Light Mode */
            body.light-mode #tutor-container .tutor-btn.btn-answer-via-chat,
            body.light-mode #tutor-container .tutor-btn.btn-answer-via-chat span,
            body.light-mode #tutor-container .tutor-btn.btn-answer-via-chat i,
            body.light-mode #tutor-container .tutor-btn.btn-exit-chat,
            body.light-mode #tutor-container .tutor-btn.btn-exit-chat span,
            body.light-mode #tutor-container .tutor-btn.btn-exit-chat i {
                color: #000000 !important;
            }
            
            body.light-mode #tutor-container .tutor-btn.btn-answer-via-chat,
            body.light-mode #tutor-container .tutor-btn.btn-exit-chat {
                border: 1px solid #000000 !important;
            }
            
            /* Generate Diagnostic Test button - black text and black border in Light Mode */
            body.light-mode #tutor-container #tutorSubmitBtn,
            body.light-mode #tutor-container #tutorSubmitBtn span,
            body.light-mode #tutor-container #tutorSubmitBtn i {
                color: #000000 !important;
            }
            
            body.light-mode #tutor-container #tutorSubmitBtn {
                border: 1px solid #000000 !important;
            }
            
            /* Confidence slider - subtle dark shadow beneath in Light Mode */
            body.light-mode #tutor-container .tutor-confidence-slider {
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
            }
            
            /* Next and Back buttons - black text and black border in Light Mode */
            body.light-mode #tutor-container .tutor-btn-back,
            body.light-mode #tutor-container .tutor-btn-back span,
            body.light-mode #tutor-container .tutor-btn-back i {
                color: #000000 !important;
            }
            
            body.light-mode #tutor-container .tutor-btn-back {
                border: 1px solid #000000 !important;
            }
            
            body.light-mode #tutor-container .tutor-nav-buttons .tutor-btn,
            body.light-mode #tutor-container .tutor-nav-buttons .tutor-btn span,
            body.light-mode #tutor-container .tutor-nav-buttons .tutor-btn i {
                color: #000000 !important;
            }
            
            body.light-mode #tutor-container .tutor-nav-buttons .tutor-btn {
                border: 1px solid #000000 !important;
            }
            
            /* Catch-all for any remaining text in character panel */
            body.light-mode #character-panel p,
            body.light-mode #character-panel span,
            body.light-mode #character-panel label,
            body.light-mode #character-panel h1,
            body.light-mode #character-panel h2,
            body.light-mode #character-panel h3,
            body.light-mode #character-panel h4,
            body.light-mode #character-panel h5,
            body.light-mode #character-panel li {
                color: #000000 !important;
            }
            
            /* ==========================================
               BIO PANEL - Light Mode Overrides
               ========================================== */
            
            /* Bio panel header */
            body.light-mode .bio-panel .bio-panel-header,
            body.light-mode .bio-panel .bio-panel-title,
            body.light-mode .bio-panel .bio-panel-close,
            body.light-mode .bio-panel .bio-panel-close i {
                color: #000000 !important;
            }
            
            /* Bio section titles and headers */
            body.light-mode .bio-panel .bio-section-title,
            body.light-mode .bio-panel .bio-section-header,
            body.light-mode .bio-panel .bio-collapse-btn,
            body.light-mode .bio-panel .bio-collapse-btn i {
                color: #000000 !important;
            }
            
            /* Bio content sections */
            body.light-mode .bio-panel .bio-panel-content,
            body.light-mode .bio-panel .bio-section-content,
            body.light-mode .bio-panel .bio-section-content-wrapper {
                color: #000000 !important;
            }
            
            /* Bio quotes */
            body.light-mode .bio-panel .bio-quote,
            body.light-mode .bio-panel .bio-quote-author {
                color: #000000 !important;
            }
            
            /* Bio fun facts */
            body.light-mode .bio-panel .bio-fun-facts,
            body.light-mode .bio-panel .bio-fun-facts li {
                color: #000000 !important;
            }
            
            /* Bio stats and labels */
            body.light-mode .bio-panel .bio-stat,
            body.light-mode .bio-panel .bio-stat-label,
            body.light-mode .bio-panel .bio-stat-value,
            body.light-mode .bio-panel .bio-core-left,
            body.light-mode .bio-panel .bio-core-right {
                color: #000000 !important;
            }
            
            /* Catch-all for any remaining text in bio panel */
            body.light-mode .bio-panel p,
            body.light-mode .bio-panel span,
            body.light-mode .bio-panel label,
            body.light-mode .bio-panel h1,
            body.light-mode .bio-panel h2,
            body.light-mode .bio-panel h3,
            body.light-mode .bio-panel h4,
            body.light-mode .bio-panel h5,
            body.light-mode .bio-panel li,
            body.light-mode .bio-panel strong,
            body.light-mode .bio-panel em,
            body.light-mode .bio-panel div:not(.bio-profile-image) {
                color: #000000 !important;
            }
            
            /* ==========================================
               STORY PANEL - Light Mode Overrides
               ========================================== */
            
            body.light-mode #story-showcase-modal,
            body.light-mode #story-showcase-modal #story-showcase-title,
            body.light-mode #story-showcase-modal .story-showcase-content,
            body.light-mode #story-showcase-modal .story-showcase-body,
            body.light-mode #story-showcase-modal .story-showcase-header,
            body.light-mode #story-showcase-modal .story-showcase-close,
            body.light-mode #story-showcase-modal p,
            body.light-mode #story-showcase-modal span,
            body.light-mode #story-showcase-modal h1,
            body.light-mode #story-showcase-modal h2,
            body.light-mode #story-showcase-modal h3,
            body.light-mode #story-showcase-modal h4,
            body.light-mode #story-showcase-modal li,
            body.light-mode #story-showcase-modal div {
                color: #000000 !important;
            }
            
            /* ==========================================
               TIMELINE - Light Mode Overrides
               ========================================== */
            
            body.light-mode .timeline-event-message,
            body.light-mode .timeline-event-text,
            body.light-mode .timeline-event-content,
            body.light-mode .timeline-container,
            body.light-mode .timeline-event,
            body.light-mode .timeline-date,
            body.light-mode .timeline-title {
                color: #000000 !important;
            }
            
            body.light-mode .timeline-container p,
            body.light-mode .timeline-container span,
            body.light-mode .timeline-container div:not(.timeline-event-icon) {
                color: #000000 !important;
            }
        `;
        document.head.appendChild(styleEl);
        console.log("[SkyModeController] Theme styles injected");
    }

    setLightMode() {
        // Session-only override - no localStorage persistence
        this._userOverride = true;

        if (this._currentMode === "light") {
            console.log("[SkyModeController] Already in light mode");
            return;
        }

        console.log(
            "[SkyModeController] Switching to Light Mode (session override)",
        );
        this._currentMode = "light";

        if (window.modelViewer && window.modelViewer.setSkyMode) {
            window.modelViewer.setSkyMode("light", true);
        } else {
            console.warn("[SkyModeController] ModelViewer not available");
        }

        this.updateButtonStates();
        this.applyUITheme();
    }

    setDarkMode() {
        // Session-only override - no localStorage persistence
        this._userOverride = true;

        if (this._currentMode === "dark") {
            console.log("[SkyModeController] Already in dark mode");
            return;
        }

        console.log(
            "[SkyModeController] Switching to Dark Mode (session override)",
        );
        this._currentMode = "dark";

        if (window.modelViewer && window.modelViewer.setSkyMode) {
            window.modelViewer.setSkyMode("dark", true);
        } else {
            console.warn("[SkyModeController] ModelViewer not available");
        }

        this.updateButtonStates();
        this.applyUITheme();
    }

    toggleMode() {
        if (this._currentMode === "light") {
            this.setDarkMode();
        } else {
            this.setLightMode();
        }
    }

    resetToSystemPreference() {
        // Demo deep link: the session hour pin outlives any reset request —
        // the capture URL must keep reproducing the same lighting.
        if (
            typeof window !== "undefined" &&
            (window.LL_HOUR_PIN === "light" || window.LL_HOUR_PIN === "dark")
        ) {
            console.log("[SkyModeController] Hour pin active — ignoring reset");
            return;
        }
        console.log("[SkyModeController] Resetting to system preference");
        this._userOverride = false;

        const systemMode = this.getSystemPreference();
        if (this._currentMode !== systemMode) {
            this._currentMode = systemMode;

            if (window.modelViewer && window.modelViewer.setSkyMode) {
                window.modelViewer.setSkyMode(systemMode, true);
            }

            this.updateButtonStates();
            this.applyUITheme();
        }
    }

    applyUITheme() {
        const isLight = this._currentMode === "light";

        if (isLight) {
            document.body.classList.add("light-mode");
            document.body.classList.remove("dark-mode");
        } else {
            document.body.classList.remove("light-mode");
            document.body.classList.add("dark-mode");
        }

        // Swap character panel videos based on mode
        this.updateCharacterVideos(isLight);

        // Swap character panel Vman image based on mode
        this.updateCharacterPanelVman(isLight);

        console.log(
            "[SkyModeController] Applied UI theme:",
            isLight ? "Light" : "Dark",
        );
    }

    updateCharacterPanelVman(isLight) {
        const vmanImg = document.getElementById("character-panel-vman");
        if (vmanImg) {
            const newSrc = isLight
                ? "/static/images/Vman.png"
                : "/static/images/Vmanwhite.png";
            vmanImg.src = newSrc;
            console.log("[SkyModeController] Updated Vman image to:", newSrc);
        }
    }

    /**
     * Update character panel videos based on light/dark mode
     * @param {boolean} isLight - Whether we're switching to light mode
     */
    updateCharacterVideos(isLight) {
        try {
            const videos = document.querySelectorAll(
                "#character-panel .character-video",
            );
            console.log(
                `[SkyModeController] Found ${videos.length} character videos to update`,
            );

            videos.forEach((video) => {
                const source = video.querySelector("source");
                if (!source) return;

                const currentSrc = source.getAttribute("src");
                if (!currentSrc) return;

                // Extract the filename from the path
                const pathParts = currentSrc.split("/");
                const currentFilename = pathParts[pathParts.length - 1];

                let newFilename;

                if (isLight) {
                    // Switching to light mode - use WW videos
                    newFilename =
                        this._videoMappings[currentFilename] || currentFilename;
                } else {
                    // Switching to dark mode - use WB videos (reverse mapping)
                    newFilename =
                        this._reverseMappings[currentFilename] ||
                        currentFilename;
                }

                if (newFilename !== currentFilename) {
                    // Build the new path
                    pathParts[pathParts.length - 1] = newFilename;
                    const newSrc = pathParts.join("/");

                    console.log(
                        `[SkyModeController] Swapping video: ${currentFilename} -> ${newFilename}`,
                    );

                    // Update the source and reload video
                    source.setAttribute("src", newSrc);
                    video.load();

                    // Try to play after loading (only on desktop where videos are visible)
                    if (window.innerWidth >= 768) {
                        video.play().catch((err) => {
                            console.log(
                                "[SkyModeController] Video autoplay blocked:",
                                err.message,
                            );
                        });
                    }
                }
            });

            console.log("[SkyModeController] Character video update complete");
        } catch (error) {
            console.error(
                "[SkyModeController] Error updating character videos:",
                error,
            );
        }
    }

    /**
     * Initialize resize listener for video sync on viewport changes
     */
    initResizeListener() {
        let wasDesktop = window.innerWidth >= 768;

        window.addEventListener("resize", () => {
            const isDesktop = window.innerWidth >= 768;

            // Only re-sync videos when crossing the 768px breakpoint from mobile to desktop
            if (isDesktop && !wasDesktop) {
                console.log(
                    "[SkyModeController] Viewport crossed to desktop, re-syncing videos",
                );
                const isLight = this._currentMode === "light";
                this.updateCharacterVideos(isLight);
            }

            wasDesktop = isDesktop;
        });
    }

    updateButtonStates() {
        const toggleBtn = document.getElementById("sky-mode-toggle-btn");
        const icon = document.getElementById("sky-mode-icon");

        if (!toggleBtn || !icon) return;

        const isLight = this._currentMode === "light";
        toggleBtn.style.color = isLight ? "#000000" : "#ffffff";

        if (isLight) {
            // Light mode → show moon
            icon.innerHTML = "";
            icon.className = "fas fa-moon";
            toggleBtn.title = "Switch to Dark Mode";
        } else {
            // Dark mode → show custom sun SVG
            icon.className = "";
            icon.innerHTML = SUN_SVG;
            toggleBtn.title = "Switch to Light Mode";
        }
    }

    getMode() {
        return this._currentMode;
    }
}

const skyModeController = new SkyModeController();
export { skyModeController };
export default skyModeController;
